import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, Sparkles } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

/**
 * Public password-recovery entry point (`/forgot-password`).
 *
 * Posts to `POST /api/v1/auth/password/forgot`, which is enumeration-safe:
 * it returns `{ success: true }` for every email regardless of whether the
 * account exists. To preserve that guarantee on the client we flip to the
 * generic "if the account exists we sent a link" state on *both* success and
 * error — never surfacing whether the address was recognised.
 *
 * Standalone surface (no AppShell chrome), mirroring LoginPage.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await apiClient.post('/auth/password/forgot', { email: email.trim().toLowerCase() });
    } catch {
      // Intentionally swallowed: showing success on error too keeps the
      // flow enumeration-safe (no timing/branch leak about account state).
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted p-4">
      <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-sm">
        <Link
          to="/login"
          className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t('forgotPassword.backToLogin')}
        </Link>

        <div className="mb-6 flex items-center justify-center gap-2 text-primary">
          <Sparkles className="h-5 w-5" />
          <h1 className="text-2xl font-semibold">{t('forgotPassword.title')}</h1>
        </div>

        {submitted ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-emerald-700/60 bg-emerald-900/20 p-3 text-sm text-emerald-200">
              <Mail className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{t('forgotPassword.sentMessage')}</span>
            </div>
            <p className="text-xs text-muted-foreground">{t('forgotPassword.checkSpam')}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">{t('forgotPassword.subtitle')}</p>
            <div>
              <label className="mb-1 block text-sm font-medium" htmlFor="email">
                {t('forgotPassword.emailLabel')}
              </label>
              <Input
                id="email"
                type="email"
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={submitting}
              />
            </div>
            <Button type="submit" disabled={submitting || !email.trim()} className="w-full">
              {submitting ? t('common.saving') : t('forgotPassword.submit')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
